import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Folder, FileText, Image as ImageIcon, Download, Loader2 } from "lucide-react";

const isImage = (ct) => (ct || "").startsWith("image/");
const CAT_LABEL = { contract: "Contract", rate_card: "Rate card", invoice: "Invoice", insurance: "Insurance", other: "Document" };

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    return api.get("/documents")
      .then(({ data }) => setDocs(data.documents || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    api.get("/documents")
      .then(({ data }) => { if (!cancelled) setDocs(data.documents || []); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <span className="inline-block text-xs uppercase tracking-wider font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">Documents</span>
        <h1 className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }}>Your Documents</h1>
        <p className="text-slate-500 mt-1">Contracts, rate cards and other files your BoxHero team has shared with you.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : error ? (
        <div data-testid="documents-error" className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <p className="text-sm font-semibold text-red-700">Couldn't load your documents.</p>
          <button onClick={load} data-testid="documents-retry" className="mt-3 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-md">Retry</button>
        </div>
      ) : docs.length === 0 ? (
        <div data-testid="documents-empty" className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4"><Folder className="w-7 h-7" /></div>
          <p className="text-lg font-semibold text-slate-700">No documents shared yet</p>
          <p className="text-sm text-slate-500 mt-1">When your BoxHero team shares a contract or rate card, it'll appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-list">
          {docs.map((d) => (
            <a key={d.id} href={`${API_BASE}/documents/files/${d.storage_path}`} target="_blank" rel="noreferrer" data-testid={`document-${d.id}`}
              className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-sm transition-all flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                {isImage(d.content_type) ? <ImageIcon className="w-6 h-6" /> : <FileText className="w-6 h-6" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 truncate">{d.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{CAT_LABEL[d.category] || "Document"} · v{d.version} · {formatDate(d.created_at)}</p>
              </div>
              <Download className="w-5 h-5 text-slate-300 group-hover:text-blue-500 shrink-0" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
